<template>
    <div class="py-12">
        <h1
            v-if="title"
            class="text-2xl font-medium leading-normal tracking-small text-gray-800"
        >
            {{ title }}
        </h1>

        <slot name="header" />

        <div class="relative grid">
            <div
                class="absolute bottom-0 w-full border-b border-gray-300"
            ></div>

            <div
                ref="row"
                class="relative mt-[21px] flex flex-col overflow-x-auto border-b border-gray-300 pb-4 [scrollbar-width:none] lg:h-[46px] lg:flex-row lg:border-b-0 lg:pb-0 [&::-webkit-scrollbar]:hidden"
                :style="fadeMask"
                :data-fade="fadeEdges || undefined"
                @scroll="updateEdges"
            >
                <li
                    v-for="tab in tabs"
                    :key="tab.key"
                    :data-active="tab.key === tabKey || undefined"
                    class="z-10 flex cursor-pointer select-none items-center whitespace-nowrap px-2 py-2.5 focus:outline-hidden lg:px-4 xl:px-6"
                    :class="{
                        ' rounded-r border-l-2 border-orange-500 bg-gray-200 pl-[6px] lg:rounded-r-none lg:border-b-2 lg:border-l-0 lg:bg-gray-100 lg:px-4 lg:pb-2 xl:px-6':
                            tab.key === tabKey,
                    }"
                    @click="navigateTo(tab)"
                >
                    <component
                        :is="tab.icon"
                        class="w-5 transition duration-200 ease-in-out"
                        :class="{
                            'text-gray-600': tab.key === tabKey,
                            'text-gray-400': !(tab.key === tabKey),
                        }"
                    ></component>

                    <h2
                        class="ml-3 font-medium transition duration-200 ease-in-out lg:ml-2 xl:ml-3"
                        :class="{
                            'text-gray-600': tab.key === tabKey,
                            'text-gray-500': !(tab.key === tabKey),
                        }"
                    >
                        {{ tab.title }}
                    </h2>

                    <span v-if="tab.new_feature" class="relative">
                        <span
                            class="absolute left-3 top-1/2 -translate-y-1/2 transform"
                        >
                            <NewFeatureIndicator />
                        </span>
                    </span>
                </li>
            </div>

            <button
                v-if="fadeStart"
                type="button"
                aria-label="Scroll tabs left"
                class="absolute bottom-0 left-0 z-20 hidden h-[46px] items-center pr-2 group text-gray-500 hover:text-gray-800 focus:outline-hidden lg:flex"
                @click="scrollTabs(-1)"
            >
                <ChevronLeftIcon
                    class="size-7 rounded-full bg-white p-1 shadow-button group-focus-visible:ring-2 group-focus-visible:ring-orange-500"
                />
            </button>
            <button
                v-if="fadeEnd"
                type="button"
                aria-label="Scroll tabs right"
                class="absolute bottom-0 right-0 z-20 hidden h-[46px] items-center pl-2 group text-gray-500 hover:text-gray-800 focus:outline-hidden lg:flex"
                @click="scrollTabs(1)"
            >
                <ChevronRightIcon
                    class="size-7 rounded-full bg-white p-1 shadow-button group-focus-visible:ring-2 group-focus-visible:ring-orange-500"
                />
            </button>
        </div>
        <slot />
    </div>
</template>

<script lang="ts" setup>
import TabItem from '@/types/Settings/TabItem';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/20/solid';
import { router } from '@inertiajs/vue3';
import { PropType, computed, onBeforeUnmount, onMounted, ref } from 'vue';

defineProps({
    title: {
        type: String,
        default: '',
    },
    tabKey: {
        type: String,
        default: '',
    },
    tabs: {
        type: Array as PropType<TabItem[]>,
        default: () => [],
    },
});

/**
 * The scrollbar is hidden, so a soft fade on either edge is what tells the
 * user there are more tabs to scroll to.
 */
const FADE_PX = 48;
const FADE = `${FADE_PX}px`;

const row = ref<HTMLElement | null>(null);
const fadeStart = ref(false);
const fadeEnd = ref(false);

const updateEdges = () => {
    const el = row.value;

    if (!el) {
        return;
    }

    fadeStart.value = el.scrollLeft > 1;
    fadeEnd.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
};

const fadeEdges = computed(() =>
    [fadeStart.value && 'start', fadeEnd.value && 'end'].filter(Boolean).join(' ')
);

const fadeMask = computed(() => {
    if (!fadeStart.value && !fadeEnd.value) {
        return {};
    }

    const start = fadeStart.value ? `transparent, black ${FADE}` : 'black';
    const end = fadeEnd.value
        ? `black calc(100% - ${FADE}), transparent`
        : 'black';
    const mask = `linear-gradient(to right, ${start}, ${end})`;

    return { maskImage: mask, WebkitMaskImage: mask };
});

/** Arrow buttons are the way to hidden tabs for a mouse wheel or keyboard. */
const scrollTabs = (direction: -1 | 1) => {
    row.value?.scrollBy({
        left: direction * row.value.clientWidth * 0.7,
        behavior: 'smooth',
    });
};

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
    const active = row.value?.querySelector<HTMLElement>('[data-active]');

    if (row.value && active) {
        const overflow =
            active.offsetLeft +
            active.offsetWidth +
            FADE_PX -
            row.value.clientWidth;

        if (overflow > 0) {
            row.value.scrollLeft = overflow;
        }
    }

    updateEdges();

    if (row.value && typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(updateEdges);
        resizeObserver.observe(row.value);
    }
});

onBeforeUnmount(() => resizeObserver?.disconnect());

const navigateTo = (tab: TabItem) => {
    router.visit(tab.url, {
        preserveScroll: true,
    });
};
</script>
